import type { Locale, Localizable } from '@models/contentful';
import type { Retailer } from '@models';
/**
 * Shared folder must have no imports (except types), only pure functions and variables
 */

/**
 * Allows to check if it is a real user browser request due to "window" is not available during build process.
 * Any "window" usage must be with checking if it is a real user browser, otherwise your builds will fail.
 */
export const isBrowser = typeof window !== 'undefined';

/** Get current environment */
export const environment = process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV || 'development';

export const isProductionEnv = environment === 'production';

export const projectTag = 'bruynzeel';

export const defaultLocale: Locale = 'nl';

export const availableLocales: Locale[] = [ defaultLocale ];

export enum PAGE_TYPES {
    PDP = 'product',
    BLOGS = 'blogs',
    BLOG = 'blog',
}

export const localeSlug = (locale: Locale) => locale === defaultLocale ? '' : `/${ locale }`;

export const isLocaleAvailable = (locale: Locale | string) => availableLocales.includes(locale as Locale);

export const isContentAvailableForLocale = (content: Localizable, locale: Locale) => {
    if (!content.languages || !content.languages.locales.length)
        return true;

    return content.languages.locales.includes(locale);
};

export const breakpoints = {
    sm: '391px',
    md: '992px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
} as const;

export const retailers: Retailer[] = [
    { name: 'bruynzeel-insects', channel: 'bruynzeel_nl_insects' },
    { name: 'bruynzeel-floors', channel: 'bruynzeel_nl_floors' },
];

const toMsLocales: Record<Locale, string> = { nl: 'nl-NL', fr: 'fr-BE' };

const toApiLocales: Record<Locale, string> = { nl: 'nl_NL', fr: 'fr_BE' };

const fromMsLocales: Record<string, Locale> = { 'nl_NL': 'nl', 'nl_BE': 'nl', 'fr_BE': 'fr' };

export const toMsLocale = (locale: Locale) => toMsLocales[locale] || locale;

export const toApiLocale = (locale: Locale) => toApiLocales[locale] || locale;

export const fromMsLocale = (locale: string) => fromMsLocales[locale] || locale;

// matches loverCased APPLICATION_STAGE of ms
const getApiEnv = () => process.env.GATSBY_ALGOLIA_ENV as 'test' | 'prod' | undefined || (isProductionEnv ? 'prod' : 'test');

export const buildAlgoliaIndexName = (name: string, locale: Locale) => [
    projectTag,
    name,
    getApiEnv(),
    toApiLocale(locale)
].join('-');
